import React, { useState } from 'react';
import { AvatarBuilder } from '../avatar/AvatarBuilder';
import { useUser } from '../providers/UserProvider';
import { DEFAULT_AVATAR, AvatarConfig, INTEREST_TAGS } from '../../types/user';
import { IconPin, IconCalendar, IconUsers, IconArrowLeft } from './Icons';
import { track } from '../../analytics';
import { t } from '../../i18n';

interface Props { onComplete: (firstAction?: 'map' | 'chat' | 'pin' | 'event') => void; }

function ProgressBar({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex gap-1.5 w-full max-w-xs mx-auto">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1 rounded-full flex-1 transition-all duration-300 ${
            i <= step ? 'bg-stabeon-accent' : 'bg-white/10'
          }`}
        />
      ))}
    </div>
  );
}

export function Onboarding({ onComplete }: Props) {
  const { user, updateUser } = useUser();
  const [step, setStep] = useState(0);
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [avatar, setAvatar] = useState<AvatarConfig>(user?.avatar || DEFAULT_AVATAR);

  const [locationPrecision, setLocationPrecision] = useState<'exact' | 'approximate' | 'hidden'>('exact');
  const [bio, setBio] = useState(user?.bio || '');
  const [interests, setInterests] = useState<string[]>(user?.interests || []);

  const toggleInterest = (tag: string) => {
    setInterests(prev => prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]);
  };

  const finish = (precision: 'exact' | 'approximate' | 'hidden') => {
    track('onboarding_completed', { location_precision: precision, skipped: false, interests_count: interests.length });
    updateUser({
      avatar,
      nickname: nickname.trim() || user?.nickname,
      locationPrecision: precision,
      bio: bio.trim() || undefined,
      interests: interests.length > 0 ? interests : undefined,
      onboarded: true,
    });
    setLocationPrecision(precision);
    setStep(5);
  };

  const skipAll = () => {
    track('onboarding_completed', { location_precision: 'exact', skipped: true });
    updateUser({ avatar, nickname: nickname.trim() || user?.nickname || 'Explorer', locationPrecision: 'exact', onboarded: true });
    setStep(5);
  };

  /* Step 0 — Value Prop (what you can do) */
  if (step === 0) return (
    <div className="h-screen w-screen flex flex-col bg-stabeon-bg px-8 pt-safe">
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <img src="/logo.svg" alt="Joystance" className="w-24 h-24 mb-8 drop-shadow-[0_0_32px_rgba(124,58,237,0.5)]" />
        <h1 className="text-3xl font-extrabold text-stabeon-text mb-2 tracking-tight">
          {t('welcome_title')}
        </h1>
        <p className="text-stabeon-sub text-base leading-relaxed mb-10 max-w-[280px]">
          {t('welcome_sub')}
        </p>

        {/* Feature highlights */}
        <div className="flex flex-col gap-4 w-full max-w-xs mb-10">
          <div className="flex items-center gap-4 text-left">
            <div className="w-10 h-10 rounded-xl bg-stabeon-accent/15 flex items-center justify-center flex-shrink-0">
              <IconPin size={18} className="text-stabeon-accent" />
            </div>
            <div>
              <p className="text-stabeon-text text-sm font-semibold">Drop pins</p>
              <p className="text-stabeon-muted text-xs">Share moments that disappear in 24h</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-left">
            <div className="w-10 h-10 rounded-xl bg-stabeon-accent/15 flex items-center justify-center flex-shrink-0">
              <IconCalendar size={18} className="text-stabeon-accent" />
            </div>
            <div>
              <p className="text-stabeon-text text-sm font-semibold">Create events</p>
              <p className="text-stabeon-muted text-xs">Gather people for anything, anywhere</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-left">
            <div className="w-10 h-10 rounded-xl bg-stabeon-accent/15 flex items-center justify-center flex-shrink-0">
              <IconUsers size={18} className="text-stabeon-accent" />
            </div>
            <div>
              <p className="text-stabeon-text text-sm font-semibold">Connect nearby</p>
              <p className="text-stabeon-muted text-xs">Chat and video call with people around you</p>
            </div>
          </div>
        </div>

        <button onClick={() => setStep(1)} className="btn-primary w-full max-w-xs">
          {t('onboarding_lets_go')}
        </button>
        <button onClick={skipAll} className="text-stabeon-muted text-xs mt-4 hover:text-stabeon-text transition-colors">
          {t('onboarding_skip')}
        </button>
      </div>
    </div>
  );

  /* Step 1 — Nickname (moved before avatar — lower friction first) */
  if (step === 1) return (
    <div className="h-screen w-screen flex flex-col bg-stabeon-bg px-8 pt-safe">
      {/* Header with back + progress */}
      <div className="flex items-center gap-3 pt-4 pb-2">
        <button onClick={() => setStep(0)} className="text-stabeon-muted hover:text-stabeon-text transition-colors">
          <IconArrowLeft size={20} />
        </button>
        <div className="flex-1"><ProgressBar step={0} total={4} /></div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-stabeon-text mb-2 text-center">{t('onboarding_name_title')}</h2>
        <p className="text-stabeon-sub text-sm mb-8 text-center max-w-[260px]">
          {t('onboarding_name_sub')}
        </p>
        <input
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && nickname.trim() && setStep(2)}
          placeholder={t('onboarding_name_placeholder')}
          maxLength={32}
          autoFocus
          className="input-field w-full max-w-xs mb-4 text-center text-lg"
        />
        <button
          onClick={() => setStep(2)}
          disabled={!nickname.trim()}
          className="btn-primary w-full max-w-xs"
        >
          {t('onboarding_continue')}
        </button>
      </div>
    </div>
  );

  /* Step 2 — Avatar (simplified — show after name so it feels personalized) */
  if (step === 2) return (
    <div className="h-screen w-screen flex flex-col bg-stabeon-bg pt-safe">
      {/* Header with back + progress */}
      <div className="flex items-center gap-3 px-6 pt-4 pb-2">
        <button onClick={() => setStep(1)} className="text-stabeon-muted hover:text-stabeon-text transition-colors">
          <IconArrowLeft size={20} />
        </button>
        <div className="flex-1"><ProgressBar step={1} total={4} /></div>
        <button onClick={() => setStep(3)} className="text-stabeon-muted text-xs hover:text-stabeon-text transition-colors">
          Skip
        </button>
      </div>

      <div className="px-6 pb-2">
        <h2 className="text-2xl font-bold text-stabeon-text">Looking good, {nickname}!</h2>
        <p className="text-stabeon-sub text-sm mt-1">Customize how you appear on the map.</p>
      </div>
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <AvatarBuilder
          initial={avatar}
          onSave={a => { setAvatar(a); setStep(3); }}
          onClose={() => setStep(3)}
        />
      </div>
    </div>
  );

  /* Step 3 — Interests + Bio (for better matches) */
  if (step === 3) return (
    <div className="h-screen w-screen flex flex-col bg-stabeon-bg pt-safe">
      <div className="flex items-center gap-3 px-6 pt-4 pb-2">
        <button onClick={() => setStep(2)} className="text-stabeon-muted hover:text-stabeon-text transition-colors">
          <IconArrowLeft size={20} />
        </button>
        <div className="flex-1"><ProgressBar step={2} total={4} /></div>
        <button onClick={() => setStep(4)} className="text-stabeon-muted text-xs hover:text-stabeon-text transition-colors">
          Skip
        </button>
      </div>

      <div className="px-6 pb-3">
        <h2 className="text-2xl font-bold text-stabeon-text">Your vibe ✨</h2>
        <p className="text-stabeon-sub text-sm mt-1">Pick your interests — we use these for smarter connections.</p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {/* Bio */}
        <div className="mb-5">
          <label className="text-stabeon-muted text-xs font-semibold uppercase tracking-wider mb-2 block">
            Short bio <span className="normal-case font-normal">(optional)</span>
          </label>
          <textarea
            value={bio}
            onChange={e => setBio(e.target.value)}
            placeholder={`e.g. ${nickname.trim() || 'Explorer'} who loves coffee, hiking and random adventures…`}
            maxLength={160}
            rows={2}
            className="input-field w-full resize-none"
          />
          <p className="text-stabeon-muted text-[10px] mt-1 text-right">{bio.length}/160</p>
        </div>

        {/* Interest tags */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-stabeon-muted text-xs font-semibold uppercase tracking-wider">
              Interests
            </label>
            <span className="text-stabeon-muted text-[10px]">{interests.length} selected</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {INTEREST_TAGS.map(tag => {
              const sel = interests.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() => toggleInterest(tag)}
                  className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-150 active:scale-95 ${
                    sel
                      ? 'bg-stabeon-accent text-white shadow-[0_0_10px_rgba(124,58,237,0.4)]'
                      : 'bg-white/[0.06] text-stabeon-sub border border-stabeon-border/40 hover:border-stabeon-accent/30'
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>

        <button onClick={() => setStep(4)} className="btn-primary w-full">
          Continue →
        </button>
      </div>
    </div>
  );

  /* Step 5 — First action CTA */
  if (step === 5) {
    const actions = [
      { emoji: '🌍', title: 'Explore the map', desc: 'See who\'s around you right now', action: 'map' },
      { emoji: '💬', title: 'Join the chat', desc: 'Say hi in the Global Room', action: 'chat' },
      { emoji: '📍', title: 'Drop a pin', desc: 'Share a moment with people nearby', action: 'pin' },
      { emoji: '🎉', title: 'Create an event', desc: 'Gather people for something fun', action: 'event' },
    ];
    return (
      <div className="h-screen w-screen flex flex-col bg-stabeon-bg px-6 pt-safe">
        <div className="flex-1 flex flex-col justify-center">
          <div className="mb-8 text-center">
            <div className="text-4xl mb-3">🎉</div>
            <h2 className="text-2xl font-bold text-stabeon-text mb-2">{t('onboarding_done_title')}</h2>
            <p className="text-stabeon-sub text-sm">{t('onboarding_done_sub')}</p>
          </div>
          <div className="flex flex-col gap-3">
            {actions.map(a => (
              <button
                key={a.action}
                onClick={() => {
                  track('onboarding_first_action_chosen', { action: a.action });
                  onComplete(a.action as any);
                }}
                className="flex items-center gap-4 p-4 card hover:border-stabeon-accent/40 active:scale-[0.98] transition-all text-left"
              >
                <span className="text-2xl w-10 h-10 rounded-xl bg-stabeon-accent/10 flex items-center justify-center flex-shrink-0">{a.emoji}</span>
                <div>
                  <p className="text-stabeon-text font-semibold text-sm">{a.title}</p>
                  <p className="text-stabeon-muted text-xs mt-0.5">{a.desc}</p>
                </div>
                <svg className="ml-auto text-stabeon-muted flex-shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* Step 4 — Location (final step, clear recommendation) */
  return (
    <div className="h-screen w-screen flex flex-col bg-stabeon-bg px-8 pt-safe">
      {/* Header with back + progress */}
      <div className="flex items-center gap-3 pt-4 pb-2">
        <button onClick={() => setStep(3)} className="text-stabeon-muted hover:text-stabeon-text transition-colors">
          <IconArrowLeft size={20} />
        </button>
        <div className="flex-1"><ProgressBar step={3} total={4} /></div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 rounded-2xl bg-stabeon-accent/15 flex items-center justify-center mb-6">
          <IconPin size={28} className="text-stabeon-accent" />
        </div>
        <h2 className="text-2xl font-bold text-stabeon-text mb-2">{t('onboarding_location_title')}</h2>
        <p className="text-stabeon-sub text-sm mb-1 max-w-[260px]">
          {t('onboarding_location_sub')}
        </p>
        <p className="text-stabeon-muted text-xs mb-7 max-w-[240px]">
          {t('onboarding_location_privacy')}
        </p>
        <div className="w-full max-w-xs flex flex-col gap-3">
          <button onClick={() => finish('exact')} className="btn-primary w-full">
            {t('onboarding_location_exact')}
          </button>
          <button onClick={() => finish('approximate')} className="btn-secondary w-full text-sm">
            {t('onboarding_location_approx')}
          </button>
          <button onClick={() => finish('hidden')} className="text-stabeon-muted text-xs mt-2 hover:text-stabeon-text transition-colors underline underline-offset-2">
            {t('onboarding_location_skip')}
          </button>
        </div>
      </div>
    </div>
  );
}
